import { Link } from "@tanstack/react-router";
import { NewspaperIcon, StickyNote, Users2Icon } from "lucide-react";

import { BlueskyIcon } from "@/components/BlueskyIcon";
import { Cpm } from "@/components/CampaignAnalytics";
import { Description } from "@/components/Description";
import { FeedAvatar } from "@/components/ui/avatar";
import { ButtonAnchor, ButtonLink } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { SearchResult } from "@/lib/api";
import { atUriToBskyUrl, cn, formatBigNumber } from "@/lib/utils";

export function PublicSearchResult({ item }: { item: SearchResult }) {
  return (
    <div
      key={item.algorithm.id}
      className={cn(
        "group",
        "px-4 pt-4 pb-2",
        "border-2 border-slate-normal bg-slate-3 dark:bg-slatedark-3 rounded-xl overflow-hidden",
        "flex flex-col gap-3",
      )}
    >
      <div className="flex gap-4">
        <div className="flex gap-4 flex-1 min-w-0">
          <FeedAvatar
            feed={item.algorithm}
            className="max-w-[50px] h-full w-full"
          />
          <div className="flex-1 flex flex-col min-w-0">
            <div className="text-xl truncate instrument-serif">
              {item.algorithm.display_name}
            </div>
            <Link
              className="text-slate-dim truncate instrument-serif"
              to="/user/$userId"
              params={{ userId: item.algorithm.user_id.toString() }}
            >
              @{item.username}
            </Link>
          </div>
        </div>
        <div
          className={cn(
            "flex gap-1",
            "lg:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 group-hover:transition-opacity group-focus-within:transition-opacity",
          )}
        >
          <SimpleTooltip message="Open on Bluesky">
            <ButtonAnchor
              variant="ghost"
              size="smIcon"
              href={atUriToBskyUrl(item.algorithm.algorithm_uri)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">View algorithm</span>
              <BlueskyIcon className="h-4 w-4" />
            </ButtonAnchor>
          </SimpleTooltip>
        </div>
      </div>
      <Description className="text-sm text-slate-dim flex-1">
        {item.algorithm.description}
      </Description>
      <div className="flex gap-6 items-center justify-between group">
        <div className="flex gap-6 items-center">
          <SimpleTooltip message="Average Unique Users">
            <div className="flex gap-1 items-center text-slate-dim instrument-serif">
              <Users2Icon className="h-4 w-4" />
              {formatBigNumber(
                item.algorithm.stats.summary.avg_unique_user_count,
              )}
            </div>
          </SimpleTooltip>
          <SimpleTooltip message="Average Post Views">
            <div className="flex gap-1 items-center text-slate-dim instrument-serif">
              <StickyNote className="h-4 w-4" />
              {formatBigNumber(
                item.algorithm.stats.summary.avg_post_view_count,
              )}
            </div>
          </SimpleTooltip>
          <SimpleTooltip message="Average Feed Views">
            <div className="flex gap-1 items-center text-slate-dim instrument-serif">
              <NewspaperIcon className="h-4 w-4" />
              {formatBigNumber(
                item.algorithm.stats.summary.avg_feed_view_count,
              )}
            </div>
          </SimpleTooltip>
        </div>
        <ButtonLink
          variant="outline"
          className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 group-hover:transition-opacity group-focus-within:transition-opacity"
          size="sm"
          to="/feeds/$feedId"
          params={{ feedId: item.algorithm.id.toString() }}
        >
          View
        </ButtonLink>
      </div>
    </div>
  );
}

export function AdsResult({
  item,
  showAdsBio = false,
}: {
  item: SearchResult;
  showAdsBio?: boolean;
}) {
  const cost = (
    <button
      type="button"
      className="text-sm flex gap-1 items-center rounded-full px-2 py-1 select-none border border-orange-7 dark:border-orangedark-7 bg-orange-3 dark:bg-orangedark-3 text-orange-normal"
    >
      <Cpm status={item.algorithm} />
    </button>
  );

  return (
    <div
      className={cn(
        "group",
        "px-4 pt-4 pb-2",
        "border-2 border-slate-normal bg-slate-3 dark:bg-slatedark-3 rounded-xl overflow-hidden",
        "flex flex-col gap-3",
      )}
    >
      <div className="flex gap-4 items-start">
        <div className="flex gap-4 flex-1 min-w-0">
          <FeedAvatar
            feed={item.algorithm}
            className="max-w-[50px] h-full w-full"
          />
          <div className="flex-1 flex flex-col min-w-0 instrument-serif">
            <div className="text-xl truncate">
              {item.algorithm.display_name}
            </div>
            <Link
              className="text-slate-dim truncate instrument-serif"
              to="/user/$userId"
              params={{ userId: item.algorithm.user_id.toString() }}
            >
              @{item.username}
            </Link>
          </div>
        </div>
        <div className={cn("flex items-center gap-1")}>
          <Popover>
            <SimpleTooltip message="Cost per 1000 views">
              {item.algorithm.algorithm_bio && !showAdsBio ? (
                <PopoverTrigger asChild>{cost}</PopoverTrigger>
              ) : (
                cost
              )}
            </SimpleTooltip>
            <PopoverContent className="whitespace-pre-wrap w-[80ch]">
              {item.algorithm.algorithm_bio}
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {item.algorithm.description && (
        <Description className="text-sm text-slate-dim flex-1 line-clamp-4">
          {item.algorithm.description}
        </Description>
      )}
      {showAdsBio && item.algorithm.algorithm_bio && (
        <p className="text-sm text-slate-dim flex-1 whitespace-pre-wrap">
          {item.algorithm.algorithm_bio}
        </p>
      )}
      <div className="flex flex-wrap flex-row justify-between gap-4 sm:gap-2">
        <div className="flex gap-6 items-center">
          <SimpleTooltip message="Average Unique Users">
            <div className="flex gap-1 items-center text-slate-dim instrument-serif">
              <Users2Icon className="h-4 w-4" />
              {formatBigNumber(
                item.algorithm.stats.summary.avg_unique_user_count,
              )}
            </div>
          </SimpleTooltip>
          <SimpleTooltip message="Average Post Views">
            <div className="flex gap-1 items-center text-slate-dim instrument-serif">
              <StickyNote className="h-4 w-4" />
              {formatBigNumber(
                item.algorithm.stats.summary.avg_post_view_count,
              )}
            </div>
          </SimpleTooltip>
          <SimpleTooltip message="Average Feed Views">
            <div className="flex gap-1 items-center text-slate-dim instrument-serif">
              <NewspaperIcon className="h-4 w-4" />
              {formatBigNumber(
                item.algorithm.stats.summary.avg_feed_view_count,
              )}
            </div>
          </SimpleTooltip>
        </div>

        <div className="flex gap-2 items-center">
          <div
            className={cn(
              "flex items-center gap-1",
              "lg:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100  group-hover:transition-opacity group-focus-within:transition-opacity",
              "lg:w-0 focus-within:w-auto group-hover:w-auto",
            )}
          >
            <SimpleTooltip message="View algorithm">
              <ButtonLink
                variant="outline"
                size="sm"
                to="/ads/$feedId"
                params={{ feedId: item.algorithm.id.toString() }}
              >
                View
              </ButtonLink>
            </SimpleTooltip>
          </div>
          {showAdsBio && (
            <ButtonLink
              variant="action"
              size="sm"
              to="/app/campaigns/create"
              search={{ feeds: JSON.stringify([item.algorithm.id]) }}
            >
              Create Campaign
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  );
}
